import Box from '@mui/material/Box';
import List from '@mui/material/List';
import { useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import {
  IJobCandidateInteraction,
  IRecruiterTeamNameAutoCompleteOption,
  csrfTokenCall,
} from 'wisestep-api';
import { WsDrawer } from 'wisestep-base';

import { setDefaultAdvanceFilters } from '../../../features/candidates/utils/CandidateHelper';
import {
  transformClientRecruitersToEmailArray,
  transformIClientDTOsToStringArray,
} from '../../../features/jobs/helper';
import { ClientAutocomplete } from '../../ClientAutocomplete';
import { ClientRecruiterAutocomplete } from '../../ClientRecruiterAutocomplete';
import {
  CreatedBwDatesFilter,
  JobStatusFilter,
  JobTitleFilter,
  NoticePeriod,
  SalaryRangeFilter,
  TagsFilter,
  StageStatusFilter,
  WsAccordion,
} from '../components';
import AdvanceFilterFooterBar from '../components/AdvanceFilterFooterBar';
import BusinessUnitFilter from '../components/BusinessUnitFilter';
import JobTypeFilter from '../components/JobTypeFilter';
import LocationsFilter from '../components/LocationsFilter';
import RecruitersFilter from '../components/RecruitersFilter';
import RelocationPreferenceFilter from '../components/RelocationPreferenceFilter';
import TeamFilter from '../components/TeamFilter';
import SubmissionAdvancedFilterFormProps from '../models/SubmissionAdvanceFilterFormProps';

interface submissionAdvanceFilterProps {
  open: boolean;
  onApplyFilters: (submissionAdvancedFilterFormProps: SubmissionAdvancedFilterFormProps) => void;
  filterClose(): void;
  isStageStatus: boolean;
  defaultValues: SubmissionAdvancedFilterFormProps;
}

const WsSubmissionsAdvancedFilter = ({
  open,
  filterClose,
  onApplyFilters,
  isStageStatus,
  defaultValues,
}: submissionAdvanceFilterProps) => {
  const methods = useForm<SubmissionAdvancedFilterFormProps>({
    defaultValues: defaultValues,
  });
  const { handleSubmit, reset } = methods;
  const onFormSubmit = (data: SubmissionAdvancedFilterFormProps) => {
    onApplyFilters({
      ...data,
      clientNames: transformIClientDTOsToStringArray(data.clientList ?? []),
      clientRecruiters: transformClientRecruitersToEmailArray(data.clientRecruiterList ?? []),
    });
    filterClose();
  };

  const [token, setToken] = useState<string>('');
  const [auth, setAuth] = useState<string>('');

  useEffect(() => {
    methods.setValue('stageStatus', isStageStatus ? methods.getValues('stageStatus') : []);
  }, [isStageStatus, methods]);
  useEffect(() => {
    const getToken = async () => {
      const res = await csrfTokenCall();
      const token = res.config.headers?.['X-XSRF-TOKEN'];
      const auth = res.config.headers?.['Authorization'];
      setToken(token ?? '');
      setAuth(auth ?? '');
      return token;
    };
    getToken();
  }, []);

  return (
    <FormProvider {...methods}>
      <WsDrawer
        className="submission-advanced-filters"
        title="Advanced Filters"
        open={open}
        onClose={() => filterClose()}
        size="medium"
        headerSize="medium"
      >
        <Box
          component="form"
          role="presentation"
          onSubmit={handleSubmit(onFormSubmit)}
          onReset={() => {
            reset(setDefaultAdvanceFilters({} as IJobCandidateInteraction));
            onFormSubmit({ clientList: [], clientRecruiterList: [] });
          }}
        >
          <Box pb={7}>
            <List>
              <JobTitleFilter name="jobTitles" />
              <JobStatusFilter name="jobStatus" />
              <TagsFilter name="tags" defaultValue={methods.getValues('tags')} />
              <RelocationPreferenceFilter name="relocationPreference" />
              <WsAccordion title="Client Name(s)">
                <ClientAutocomplete name="clientList" multiple={true} />
              </WsAccordion>
              <WsAccordion title="Client Recruiter">
                <ClientRecruiterAutocomplete
                  name="clientRecruiterList"
                  multiple={true}
                  clients={methods.watch('clientList') ?? []}
                />
              </WsAccordion>
              <NoticePeriod name="tat" title="Submitted between X hours of Job Posting" />
              <JobTypeFilter name="jobType" title="Candidate Job Type" />
              <LocationsFilter
                name="jobLocations"
                title="Job Locations"
                wisestepApi={true}
                token={token}
                authorization={auth}
              />

              <LocationsFilter
                name="candidateLocations"
                title="Candidate Locations"
                wisestepApi={true}
                token={token}
                authorization={auth}
              />

              <CreatedBwDatesFilter
                name="candidateNotesLatestCreatedAtFromDate"
                name1="candidateNotesLatestCreatedAtToDate"
                title="Latest Stage Status Update Between"
              />
              <SalaryRangeFilter title="Salary Between" />
              <RecruitersFilter
                name="submittedByRecruiterAutocompleteSelects"
                title="Submitted By Team Member(s) to Assigned job"
                defaultValue={methods.getValues('submittedByRecruiterAutocompleteSelects')}
              />
              <TeamFilter
                name="teamNameAutocompleteSelects"
                title="Submitted By Team(s) to Assigned job"
                defaultValue={
                  methods
                    .getValues('teamNameAutocompleteSelects')
                    ?.map((obj) => obj.name as string) ?? []
                }
                defaultObjects={
                  methods.getValues(
                    'teamNameAutocompleteSelects'
                  ) as IRecruiterTeamNameAutoCompleteOption[]
                }
              />
              <RecruitersFilter
                name="submissionsToJobsPostedByTeamMemberAutocompleteSelects"
                title="Submitted To Jobs Posted by Team Member(s)"
                defaultValue={methods.getValues(
                  'submissionsToJobsPostedByTeamMemberAutocompleteSelects'
                )}
              />
              <TeamFilter
                name="submissionsToJobsPostedByTeamNameAutocompleteSelects"
                title="Submitted To Jobs Posted by Team(s)"
                defaultValue={
                  methods
                    .getValues('submissionsToJobsPostedByTeamNameAutocompleteSelects')
                    ?.map((obj) => obj.name as string) ?? []
                }
              />
              <CreatedBwDatesFilter
                title="Stage Date"
                name="stageDateFromDate"
                name1="stageDateToDate"
                disableFuture={false}
              />
              <StageStatusFilter
                name="stageStatus"
                stageStatus={methods.getValues('stageStatus')}
              />
              <BusinessUnitFilter name="businessUnit" />
            </List>
          </Box>
          <AdvanceFilterFooterBar />
        </Box>
      </WsDrawer>
    </FormProvider>
  );
};
export default WsSubmissionsAdvancedFilter;
